import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { PlaneTakeoff } from '@/components/icons'
import { FlightLogDrawer } from './FlightLogDrawer'
import { FlightLogsAttentionDrawer } from './FlightLogsAttentionDrawer'
import { useIsPilot } from '../hooks/useIsPilot'
import { useFlightLogGetMyPendingLogs, type PendingFlightLog } from '../lib/hooks'
import { hapticLight } from '@/lib/haptics'

/**
 * Home-screen flight log card — pilots only. One card for everything the
 * pilot still owes a technical log for (incl. a leg departing within ~2h):
 * - exactly one log: route-detail style with a Start/Finish pill, tapping
 *   opens that log's FlightLogDrawer directly
 * - more than one: aggregate "Flight Logs — N need completing" style,
 *   tapping opens the attention drawer listing them all
 */
export function HomeFlightLogCard() {
  const navigate = useNavigate()
  const { isPilot } = useIsPilot()
  const { data } = useFlightLogGetMyPendingLogs(isPilot)
  const logs = (data as PendingFlightLog[] | undefined) ?? []
  // Log sticks around after close so the drawer can animate out
  const [selectedLog, setSelectedLog] = useState<PendingFlightLog | null>(null)
  const [isLogOpen, setIsLogOpen] = useState(false)
  const [isListOpen, setIsListOpen] = useState(false)

  if (!isPilot || logs.length === 0) return null

  const single = logs.length === 1 ? logs[0] : null
  const kind = single?.lifecycle === 'notStarted' ? 'start' : 'finish'

  return (
    <>
      {single ? (
        <button
          type="button"
          onClick={() => {
            hapticLight()
            setSelectedLog(single)
            setIsLogOpen(true)
          }}
          className="flex items-center justify-between gap-3 bg-background-brand-subtle border border-border-brand-bold rounded-2xl px-4 py-4 shadow-[0px_5px_12.5px_rgba(0,0,0,0.05)]"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="bg-background-brand-bold rounded-lg p-2 flex shrink-0 items-center justify-center">
              <PlaneTakeoff className="w-8 h-8 text-content-primary-inverse" />
            </div>
            <div className="flex min-w-0 flex-col items-start">
              <p className="truncate max-w-full text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px]">
                Flight log — {single.departureAirportIcao} → {single.arrivalAirportIcao}
                {single.aircraftRegistration && (
                  <span className="font-medium text-content-primary-subtle">
                    {' · '}
                    {single.aircraftRegistration}
                  </span>
                )}
              </p>
              <p className="text-left text-sm text-content-primary-subtle leading-5">
                {kind === 'start'
                  ? 'Transfer the flight log from the paper folio.'
                  : 'Ready for engine hours, fuel, and sign-off.'}
              </p>
            </div>
          </div>
          {/* Pill CTA — styled span, the whole card is the button */}
          <span className="shrink-0 rounded-full bg-background-primary-bold px-3 py-1.5 text-xs font-semibold text-content-primary-inverse">
            {kind === 'start' ? 'Start log' : 'Finish log'}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => {
            hapticLight()
            setIsListOpen(true)
          }}
          className="flex items-center justify-between gap-3 bg-background-brand-subtle border border-border-brand-bold rounded-2xl px-4 py-4 shadow-[0px_5px_12.5px_rgba(0,0,0,0.05)]"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="bg-background-brand-bold rounded-lg p-2 flex shrink-0 items-center justify-center">
              <PlaneTakeoff className="w-8 h-8 text-content-primary-inverse" />
            </div>
            <div className="flex min-w-0 flex-col items-start">
              <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px]">
                Flight Logs
              </p>
              <p className="text-sm text-content-primary-subtle leading-5">
                {logs.length} need completing.
              </p>
            </div>
          </div>
          <span className="flex h-[25px] min-w-[25px] shrink-0 items-center justify-center rounded-lg bg-surface-L2 px-[9px] text-xs font-medium text-content-primary-bold">
            {logs.length}
          </span>
        </button>
      )}

      {selectedLog && (
        <FlightLogDrawer
          isOpen={isLogOpen}
          onClose={() => setIsLogOpen(false)}
          tripLegId={selectedLog.tripLegId}
          initialSection={selectedLog.defectDeferred ? 'defects' : undefined}
          onViewTrip={() => {
            const tripId = selectedLog.tripId
            navigate({
              to: '/schedule/$tripId',
              params: { tripId },
              search: { legId: selectedLog.tripLegId },
            } as any)
          }}
        />
      )}

      <FlightLogsAttentionDrawer open={isListOpen} onOpenChange={setIsListOpen} />
    </>
  )
}
